import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CreateLiPeople,
	EnrichedSuggestions,
	EnrichedSuggestionsSchema,
	LiPeople,
	LiPeopleResponse,
	LiPeopleResponseSchema,
	LiPeopleSchema,
	Suggestion,
	SuggestionSchema,
	Suggestions,
	SuggestionsSchema,
} from "../../pages/LiPeople/schema/liPeople";

export const liPeopleApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getLiPeoples: builder.query<LiPeopleResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "liPeople",
				params: {
					...(params && { ...params }),
				},
				responseSchema: LiPeopleResponseSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		geSuggestion: builder.query<Suggestions, { type: string; keyword: string }>({
			query: ({ type, keyword }) => ({
				// Encoded: "Frankfurt/Main" would add a path segment and "C#" lose its "#" (PREC-4539).
				url: `liPeople/suggest/${type}/${encodeURIComponent(keyword)}`,
				responseSchema: SuggestionsSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		getPersonSuggestion: builder.query<
			Suggestions,
			{ keyword: string; trackerOrganizationId?: number }
		>({
			query: ({ keyword }) => ({
				url: `liPeople/suggest/person/${keyword}`,
				responseSchema: SuggestionsSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		getCompanySuggestion: builder.query<Suggestions, { keyword: string }>({
			query: ({ keyword }) => ({
				url: `liPeople/suggest/company/${keyword}`,
				responseSchema: SuggestionsSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		getEnrichedPersonSuggestion: builder.query<EnrichedSuggestions, { keyword: string }>({
			query: ({ keyword }) => ({
				url: `liPeople/profileSuggest/person/${keyword}`,
				responseSchema: EnrichedSuggestionsSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		getEnrichedCompanySuggestion: builder.query<EnrichedSuggestions, { keyword: string }>({
			query: ({ keyword }) => ({
				url: `liPeople/profileSuggest/company/${keyword}`,
				responseSchema: EnrichedSuggestionsSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		resolveEnrichedPersonSuggestion: builder.query<Suggestion, { id: string }>({
			query: ({ id }) => ({
				url: `liPeople/profileResolve/person/${id}`,
				responseSchema: SuggestionSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		resolveEnrichedCompanySuggestion: builder.query<Suggestion, { id: string }>({
			query: ({ id }) => ({
				url: `liPeople/profileResolve/company/${id}`,
				responseSchema: SuggestionSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		resolveEnrichedPersonByUrl: builder.query<Suggestion, { url: string }>({
			query: ({ url }) => ({
				url: `liPeople/profileResolveByUrl/person`,
				params: { url },
				responseSchema: SuggestionSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		resolveEnrichedCompanyByUrl: builder.query<Suggestion, { url: string }>({
			query: ({ url }) => ({
				url: `liPeople/profileResolveByUrl/company`,
				params: { url },
				responseSchema: SuggestionSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		getLiPeople: builder.query<LiPeople, number>({
			query: (id: number) => ({
				url: `liPeople/${id}`,
				params: {
					id,
				},
				responseSchema: LiPeopleSchema,
			}),
			providesTags: ["LiPeople"],
		}),
		createLiPeople: builder.mutation<Partial<LiPeople>, Partial<CreateLiPeople>>({
			query: (body) => ({
				url: `liPeople`,
				method: "POST",
				body,
				responseSchema: LiPeopleSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiPeople"];
				}

				return ["LiPeople"];
			},
		}),
		updateLiPeople: builder.mutation<Partial<LiPeople>, Partial<CreateLiPeople>>({
			query: ({ id, ...body }) => ({
				url: `liPeople/${id}`,
				method: "PATCH",
				body,
				responseSchema: LiPeopleSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiPeople"];
				}

				return ["LiPeople"];
			},
		}),
		deleteLiPeople: builder.mutation<Partial<LiPeople>, { id: number; listId?: number }>({
			query: ({ id, listId }) => ({
				url: listId ? `liPeople/${id}/list/${listId}` : `liPeople/${id}`,
				method: "DELETE",
				responseSchema: LiPeopleSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiPeople"];
				}

				return ["LiPeople"];
			},
		}),
	}),
});

export const {
	useGetLiPeoplesQuery,
	useLazyGeSuggestionQuery,
	useLazyGetLiPeoplesQuery,
	useLazyGetPersonSuggestionQuery,
	useLazyGetCompanySuggestionQuery,
	useLazyGetEnrichedPersonSuggestionQuery,
	useLazyGetEnrichedCompanySuggestionQuery,
	useLazyResolveEnrichedPersonSuggestionQuery,
	useLazyResolveEnrichedCompanySuggestionQuery,
	useLazyResolveEnrichedPersonByUrlQuery,
	useLazyResolveEnrichedCompanyByUrlQuery,
	useGetLiPeopleQuery,
	useUpdateLiPeopleMutation,
	useDeleteLiPeopleMutation,
	useCreateLiPeopleMutation,
} = liPeopleApi;
export const accountApiReducer = liPeopleApi.reducer;
export const accountApiMiddleware = liPeopleApi.middleware;
